import { match, P } from 'ts-pattern'
import { DATA_BINDING } from '@wix/app-definition-ids'
import { createCMSWebDeeplinkBuilder } from '@wix/cms-web-deeplinks'
import type Experiments from '@wix/wix-experiments'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type WixData from '@wix/wix-data-core/dist/types/data-api'
import type { AppApi } from '@wix/wix-data-client-common'
import {
  AUTO_CMS_EDITOR_PANEL_BASENAME,
  findFirstItemMatchingDynamicPage,
  getInnerRouteForDynamicPageItem,
  getPageLinkField,
  getCMSWebAppDashboardSlug,
  isEditor3OrStudio2,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  Logger,
  I18n,
} from '@wix/wix-data-client-common-standalone'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import { checkIfRoutePatternIncludesFields } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/checkIfRoutePatternIncludesFields'
import { getCollectionIdAndPatternOfCurrentDynamicPage } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/getCollectionIdAndPatternOfCurrentDynamicPage'

export interface AutoCMSPathParts {
  collectionId?: string
  isNew?: boolean
  itemId?: string
  settings?: boolean
  collectionsGroup?: string
  importRequested?: boolean
}

export interface AutoCMSPathDescriptor {
  parts: AutoCMSPathParts
  origin?: string
  hostname?: string
  locale?: string
  editorSdkProxy?: EditorSDK
}

const autoCMSPathDescriptor = ({
  parts: {
    collectionId,
    isNew,
    itemId,
    settings,
    collectionsGroup,
    importRequested,
  } = {},
  origin,
  hostname,
  locale,
  editorSdkProxy,
}: {
  parts?: AutoCMSPathParts
  origin?: string
  hostname?: string
  locale?: string
  editorSdkProxy?: EditorSDK
}): AutoCMSPathDescriptor => ({
  parts: {
    collectionId,
    isNew,
    itemId,
    settings,
    collectionsGroup,
    importRequested,
  },
  origin,
  hostname,
  locale,
  editorSdkProxy,
})

const autoCMSEditorPanelURL = async (
  {
    parts: {
      collectionId,
      isNew,
      itemId,
      settings,
      collectionsGroup,
      importRequested,
    },
    origin,
    hostname,
    editorSdkProxy,
  }: AutoCMSPathDescriptor,
  appApi: AppApi,
): Promise<string> => {
  const params: Record<string, string | boolean | undefined> = { origin }

  if (typeof collectionId === 'string') {
    params.collectionId = encodeURIComponent(collectionId)

    if (isNew) {
      params.isNew = true
    } else if (typeof itemId === 'string') {
      params.itemId = encodeURIComponent(itemId)
    } else if (importRequested) {
      params.importRequested = true
    }
  } else if (settings) {
    params.settings = true
  } else if (typeof collectionsGroup === 'string') {
    params.collectionsGroup = encodeURIComponent(collectionsGroup)
  }

  const instance = await appApi.getInstance()
  params.instance = instance

  const panelURL = await getPanelUrl({
    hostname,
    panelBasename: AUTO_CMS_EDITOR_PANEL_BASENAME,
    relativeUrl: './editor-panel-app.html',
    params,
    editorSdkProxy,
    panelWithSeparateGaLifecycle: true,
  })

  return panelURL
}

interface BuildCMSWebDeeplinkParams extends AutoCMSPathDescriptor {
  sdkVersion?: string
  experimentsManager: Experiments
  bmOverEditor: boolean
}

const buildCMSWebDeeplink = ({
  parts: {
    collectionId,
    isNew,
    itemId,
    settings,
    collectionsGroup,
    importRequested,
  } = {},
  origin,
  locale,
  sdkVersion,
  experimentsManager,
  bmOverEditor,
}: BuildCMSWebDeeplinkParams): string => {
  const builder = createCMSWebDeeplinkBuilder()

  const routeBuilder = match({
    collectionId,
    isNew,
    itemId,
    settings,
    collectionsGroup,
    importRequested,
  })
    .with({ collectionId: P.string, isNew: true }, ({ collectionId }) =>
      builder.newCollectionItem(collectionId),
    )
    .with(
      { collectionId: P.string, itemId: P.string },
      ({ collectionId, itemId }) =>
        builder.collectionItem(collectionId, itemId),
    )
    .with(
      { collectionId: P.string, importRequested: true },
      ({ collectionId }) => builder.collection(collectionId).withImportItems(),
    )
    .with({ collectionId: P.string }, ({ collectionId }) =>
      builder.collection(collectionId),
    )
    .with({ settings: true }, () => builder.storageSettings())
    .with({ collectionsGroup: P.string }, ({ collectionsGroup }) =>
      builder.collections().withExpandedCollectionsGroup(collectionsGroup),
    )
    .otherwise(() => builder.collections())

  routeBuilder.withAppDefinitionId(DATA_BINDING)

  if (sdkVersion) {
    routeBuilder.withSDKVersion(sdkVersion)
  }
  if (origin) {
    routeBuilder.withOrigin(origin)
  }
  if (locale) {
    routeBuilder.withLocale(locale)
  }
  if (bmOverEditor) {
    routeBuilder.withBmOverEditor(true)
  }
  if (process.env.WIX_DATA_CLIENT_DEBUG === 'true') {
    routeBuilder.withDebug()
  }

  const appSlug = getCMSWebAppDashboardSlug(experimentsManager.all())
  const basePath = `/${appSlug}`

  return routeBuilder.buildPath(basePath)
}

export interface OpenAutoCMSPanelParams {
  editorSdkProxy: EditorSDK
  pathDescriptor: AutoCMSPathDescriptor
  i18n: I18n
  appApi: AppApi
  experimentsManager: Experiments
  sdkVersion?: string
}

const openAutoCMSPanel = async ({
  editorSdkProxy,
  pathDescriptor,
  i18n,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSPanelParams) => {
  const dpVariantsEnabled = experimentsManager.enabled(
    'specs.wixDataClient.dynamicPageVariants',
  )
  const bmOverEditor =
    isEditor3OrStudio2(await appApi.getEditorType()) ||
    experimentsManager.enabled('specs.wixCode.CMSWebInBMOverEditor')
  if (!editorSdkProxy.appBuilder && bmOverEditor) {
    const url = buildCMSWebDeeplink({
      parts: pathDescriptor.parts,
      origin: pathDescriptor.origin,
      locale: pathDescriptor.locale,
      sdkVersion,
      experimentsManager,
      bmOverEditor,
    })

    const result = await editorSdkProxy.editor.openDashboardPanel({
      url,
      closeOtherPanels: false,
    })

    appApi.emit('contentManagerClosed')
    if (dpVariantsEnabled) {
      editorSdkProxy.editor.routers.refresh()
    }

    return result
  }

  const result = await editorSdkProxy.editor.openModalPanel({
    title: i18n.t('Content_Manager_Panel_Title'),
    url: await autoCMSEditorPanelURL(pathDescriptor, appApi),
    width: '94%' as unknown as number,
    height: '94%' as unknown as number,
    centered: true,
    noPanelPadding: true,
  } as Parameters<typeof editorSdkProxy.editor.openModalPanel>[0])

  appApi.emit('contentManagerClosed')
  if (dpVariantsEnabled) {
    editorSdkProxy.editor.routers.refresh()
  }

  return result
}

interface InternalOpenAutoCMSParams extends OpenAutoCMSPanelParams {
  logger: Logger
}

const _openAutoCMS = async ({
  editorSdkProxy,
  pathDescriptor,
  i18n,
  logger,
  appApi,
  experimentsManager,
  sdkVersion,
}: InternalOpenAutoCMSParams) => {
  const result = await openAutoCMSPanel({
    editorSdkProxy,
    pathDescriptor,
    i18n,
    appApi,
    experimentsManager,
    sdkVersion,
  }).catch(error => {
    logger.error(new Error(`Failed to open auto CMS panel`, { cause: error }), {
      pathDescriptor,
    })
  })

  void appApi.refreshCollectionItemCounts()

  void appApi.refreshSchemasCache()

  void updateEditorView(editorSdkProxy)

  return result
}

interface OpenAutoCMSAndUpdateInnerRouteAfterClosingParams {
  wixData: WixData
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  buildPathDescriptor: (parts: {
    collectionId?: string
    itemId?: string
  }) => AutoCMSPathDescriptor
  i18n: I18n
  logger: Logger
  appApi: AppApi
  experimentsManager: Experiments
  sdkVersion?: string
}

const openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded = async ({
  wixData,
  editorSdkProxy,
  collectionsApi,
  buildPathDescriptor,
  i18n,
  logger,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSAndUpdateInnerRouteAfterClosingParams) => {
  const fallbackPathParts: { collectionId?: string; itemId?: string } = {}

  const collectionIdAndPattern =
    await getCollectionIdAndPatternOfCurrentDynamicPage({ editorSdkProxy })
  const currentPageIsValidDynamicPage = collectionIdAndPattern !== null

  if (currentPageIsValidDynamicPage) {
    const { collectionId, pattern } = collectionIdAndPattern
    Object.assign(fallbackPathParts, { collectionId })

    const routePatternIncludesFields =
      checkIfRoutePatternIncludesFields(pattern)
    // If a pattern does not include a field, the page is considered an index page.
    // All items have the same links to an index page so the inner route does not change as a result of changing an item matching the dynamic page.
    if (routePatternIncludesFields) {
      const dynamicRouting =
        await editorSdkProxy.document.routers.getCurrentDynamicRouting()

      const { innerRoute, prefix } = dynamicRouting || {}
      const innerRouteIsDefined = typeof innerRoute !== 'undefined'
      // The inner route can be undefined for different reasons:
      // - there are no items in the collection,
      // - there are items in the collection but the routing data has not yet been loaded,
      // - the dynamic page is broken (there is no route pattern for the page).
      if (innerRouteIsDefined) {
        let pageLinkField
        try {
          const fields = (await collectionsApi.get(collectionId)).fields
          pageLinkField = getPageLinkField(fields, prefix, pattern)
        } catch {
          pageLinkField = null
        }
        const item = await findFirstItemMatchingDynamicPage({
          collectionId,
          prefix,
          innerRoute,
          wixData,
          pageLinkField,
        })
        if (item) {
          const itemId = item._id

          const valueReturnedByAutoCMS = await _openAutoCMS({
            editorSdkProxy,
            pathDescriptor: buildPathDescriptor({ collectionId, itemId }),
            i18n,
            logger,
            appApi,
            experimentsManager,
            sdkVersion,
          })

          let changedInnerRouteIfAny

          const innerRouteOfItem = await getInnerRouteForDynamicPageItem({
            collectionId,
            itemId,
            pageLinkField: pageLinkField ?? undefined,
            prefix,
            wixData,
          })
          if (
            typeof innerRouteOfItem !== 'undefined' &&
            innerRouteOfItem !== innerRoute
          ) {
            changedInnerRouteIfAny = innerRouteOfItem
          }

          await updateEditorView(editorSdkProxy, changedInnerRouteIfAny)

          return valueReturnedByAutoCMS
        }
      }
    }
  }

  return _openAutoCMS({
    editorSdkProxy,
    pathDescriptor: buildPathDescriptor(fallbackPathParts),
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })
}

interface OpenAutoCMSViewParams {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  collectionId?: string
  itemId?: string
  origin: string
  wixData: WixData
  hostname: string
  i18n: I18n
  logger: Logger
  appApi: AppApi
  experimentsManager: Experiments
  sdkVersion?: string
  importRequested?: boolean
  collectionsGroup?: string
}

export const openAutoCMSNewItemView = ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSViewParams) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionId, isNew: true },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })

export const openWixServices = async ({
  editorSdkProxy,
}: {
  editorSdkProxy: EditorSDK
}) => {
  await editorSdkProxy.editor.openDashboardPanel({
    url: '/wix-services',
    closeOtherPanels: false,
  })

  void updateEditorView(editorSdkProxy)
}

export const openAutoCMSCollectionView = ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
  importRequested,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSViewParams) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionId, importRequested },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })

export const openAutoCMSCollectionsList = ({
  editorSdkProxy,
  collectionsApi,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  collectionsGroup,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSViewParams) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionsGroup },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })

export const openAutoCMSAdvancedSettings = ({
  editorSdkProxy,
  collectionsApi,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSViewParams) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { settings: true },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })

export const openAutoCMSItemView = ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  itemId,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSViewParams) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionId, itemId },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })

export const openAutoCMSItemViewForCurrentDynamicPage = async ({
  editorSdkProxy,
  collectionsApi,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
  experimentsManager,
  sdkVersion,
}: OpenAutoCMSViewParams) => {
  return openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    wixData,
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: ({ collectionId, itemId }) =>
      autoCMSPathDescriptor({
        parts: { collectionId, itemId },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    i18n,
    logger,
    appApi,
    experimentsManager,
    sdkVersion,
  })
}

function updateEditorView(editorSdkProxy: EditorSDK, selectedRoute?: string) {
  return editorSdkProxy.editor.routers
    .refresh({
      selectedRoute,
    })
    .then(() => {
      //TODO: check do we need it in TB
      editorSdkProxy.document.application.livePreview.refresh({
        source: 'AFTER_DB_CHANGE',
        shouldFetchData: true,
      })
      editorSdkProxy.editor.updateAppState({
        lastDataUpdateAt: Date.now(),
      })
    })
    .catch(() => {}) // Editor X does not have implementation for it yet
}
